import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { useSocketEvent } from "socket.io-react-hook";
import { ArbitrageCryptoOrderBooks, Paginated } from "../types";
import { useCryptoDetailsSocket } from "../hooks/useCryptoDetailsSocket";

// TTL for drawer data - invalidate if no updates received within this time
const DATA_TTL_MS = 30000; // 30 seconds

interface ProfitDrawerContextProps {
  data: ArbitrageCryptoOrderBooks | null;
  subscribeToCrypto: (id: string | null, symbol?: string) => void;
  currentCryptoId: string | null;
  isStale: boolean;
  lastUpdateTime: number | null;
}

const ProfitDrawerContext = createContext<ProfitDrawerContextProps | undefined>(
  undefined
);

export const ProfitDrawerProvider = ({ children }: { children: ReactNode }) => {
  // Use the dedicated crypto details socket (orderbook-aggregator)
  const { socket, isConnected } = useCryptoDetailsSocket();

  const { lastMessage } = useSocketEvent<Paginated<ArbitrageCryptoOrderBooks>>(
    socket,
    "details_updates"
  );

  const { sendMessage: sendSubscribe } = useSocketEvent<
    Paginated<ArbitrageCryptoOrderBooks>
  >(socket, "subscribe_crypto");
  const { sendMessage: sendUnSubscribe } = useSocketEvent<
    Paginated<ArbitrageCryptoOrderBooks>
  >(socket, "unsubscribe_crypto");

  const [currentCryptoId, setCurrentCryptoId] = useState<string | null>(null);
  const [lastUpdateTime, setLastUpdateTime] = useState<number | null>(null);
  const [isStale, setIsStale] = useState(false);
  const [validData, setValidData] = useState<ArbitrageCryptoOrderBooks | null>(null);
  const staleCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Track when we receive new data
  useEffect(() => {
    if (lastMessage?.data) {
      const now = Date.now();
      setLastUpdateTime(now);
      setIsStale(false);
      setValidData(lastMessage.data);
    }
  }, [lastMessage]);

  // Check for stale data periodically
  useEffect(() => {
    if (currentCryptoId) {
      staleCheckIntervalRef.current = setInterval(() => {
        if (lastUpdateTime && Date.now() - lastUpdateTime > DATA_TTL_MS) {
          console.log("[ProfitDrawer] Data is stale, invalidating...");
          setIsStale(true);
          setValidData(null); // Clear stale data
        }
      }, 5000); // Check every 5 seconds
    }

    return () => {
      if (staleCheckIntervalRef.current) {
        clearInterval(staleCheckIntervalRef.current);
      }
    };
  }, [currentCryptoId, lastUpdateTime]);

  // Clear data when unsubscribing
  useEffect(() => {
    if (!currentCryptoId) {
      setValidData(null);
      setLastUpdateTime(null);
      setIsStale(false);
    }
  }, [currentCryptoId]);

  console.log("λ  - ProfitDrawerProvider - currentCryptoId:", currentCryptoId, "isStale:", isStale);

  const debouncedSendCryptoMessage = useCallback(
    (type: "subscribe_crypto" | "unsubscribe_crypto", id: string, symbol?: string) => {
      if (isConnected) {
        if (type === "subscribe_crypto") {
          sendSubscribe({ id, symbol });
        } else if (type === "unsubscribe_crypto") {
          sendUnSubscribe({ id });
        }
      }
    },
    [sendUnSubscribe, sendSubscribe, isConnected]
  );

  const subscribeToCrypto = useCallback(
    (id: string | null, symbol?: string) => {
      currentCryptoId &&
        debouncedSendCryptoMessage("unsubscribe_crypto", currentCryptoId);

      setCurrentCryptoId(null);
      if (id) {
        debouncedSendCryptoMessage("subscribe_crypto", id, symbol);
        setCurrentCryptoId(id);
      }
    },
    [debouncedSendCryptoMessage, currentCryptoId]
  );

  return (
    <ProfitDrawerContext.Provider
      value={{
        data: validData,
        subscribeToCrypto,
        currentCryptoId,
        isStale,
        lastUpdateTime,
      }}
    >
      {children}
    </ProfitDrawerContext.Provider>
  );
};

export const useProfitDrawer = () => {
  const context = useContext(ProfitDrawerContext);
  if (!context) {
    throw new Error(
      "useProfitDrawer must be used within a ProfitDrawerProvider"
    );
  }
  return context;
};
